import React, { useState } from 'react';
import {
  Box,
  Fab,
  Drawer,
  Typography,
  TextField,
  Button,
  Divider,
  Chip,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import { useOrganization, useDevOrgSwitcher } from '../contexts/OrganizationContext';

/**
 * Development-only component to switch between organizations
 * Only shown in development mode
 */
export const DevOrgSwitcher: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [slug, setSlug] = useState('');
  const { currentOrg } = useOrganization();
  const { switchToOrg } = useDevOrgSwitcher();

  // Only show in development
  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  const handleSwitch = async () => {
    if (slug.trim()) {
      await switchToOrg(slug.trim());
      setSlug('');
      setOpen(false);
    }
  };

  const quickSwitchOrgs = [
    { slug: 'spl', name: 'SPL (League)' },
    { slug: 'padel-eagle', name: 'Padel Eagle (Tournaments)' },
  ];

  return (
    <>
      <Fab
        color="secondary"
        aria-label="dev-settings"
        sx={{
          position: 'fixed',
          bottom: { xs: 140, sm: 80 },
          right: 16,
          zIndex: 9999,
        }}
        onClick={() => setOpen(true)}
      >
        <SettingsIcon />
      </Fab>

      <Drawer anchor="right" open={open} onClose={() => setOpen(false)}>
        <Box sx={{ width: 350, p: 3 }}>
          <Typography variant="h6" gutterBottom>
            🔧 Dev: Organization Switcher
          </Typography>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom>
            Current Organization:
          </Typography>
          {currentOrg ? (
            <Box sx={{ mb: 3 }}>
              <Chip label={currentOrg.slug} color="primary" sx={{ mb: 1 }} />
              <Typography variant="body2">{currentOrg.name}</Typography>
              <Typography variant="caption" color="textSecondary">
                Type: {currentOrg.competitionType}
              </Typography>
              <br />
              <Typography variant="caption" color="textSecondary">
                Plan: {currentOrg.subscription.plan}
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" color="error" sx={{ mb: 3 }}>
              No organization loaded
            </Typography>
          )}

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom>
            Quick Switch:
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 3 }}>
            {quickSwitchOrgs.map((org) => (
              <Button
                key={org.slug}
                variant="outlined"
                size="small"
                onClick={() => {
                  switchToOrg(org.slug);
                  setOpen(false);
                }}
                disabled={currentOrg?.slug === org.slug}
              >
                {org.name}
              </Button>
            ))}
          </Box>

          <Divider sx={{ my: 2 }} />

          <Typography variant="subtitle2" gutterBottom>
            Switch by Slug:
          </Typography>
          <TextField
            fullWidth
            size="small"
            label="Organization Slug"
            value={slug}
            onChange={(e) => setSlug(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSwitch()}
            sx={{ mb: 2 }}
          />
          <Button variant="contained" fullWidth onClick={handleSwitch}>
            Switch Organization
          </Button>

          <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="caption" color="textSecondary">
              💡 Tip: Use the script below to create test organizations in
              Firestore for development.
            </Typography>
          </Box>
        </Box>
      </Drawer>
    </>
  );
};
